import * as React from "react";
import { ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type MultiSelectOption = {
  label: string;
  value: string | number;
  colorCode?: string;
};

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  placeholder?: string;
  loading?: boolean;
  isRequired?: boolean;
  isChipsRequired?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options",
  loading = false,
  isRequired = true,
  isChipsRequired = true,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const toggleOption = (value: string | number) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const removeOption = (value: string | number) => {
    onChange(selected.filter((v) => v !== value));
  };

  const selectedOptions = options.filter((opt) => selected.includes(opt.value));

  return (
    <div className="flex flex-col gap-2 w-full relative">
      {isRequired && (
        <input
          type="text"
          required
          hidden
          value={selected.length ? "filled" : ""}
          readOnly
        />
      )}
      {isChipsRequired && selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1 w-[calc(100%-74px)] absolute top-[3px] left-[12px] h-[26px] overflow-y-auto">
          {selectedOptions.map((opt) => (
            <Badge
              key={opt.value}
              variant="secondary"
              className="flex items-center gap-1 h-[24px] pl-2 pr-1 hover:bg-[#DCDFEC] rounded-[4px]"
            >
              {opt.colorCode && (
                <span
                  className="w-4 h-4 bg-[#f5f5f4] rounded-[2px] border"
                  style={{ backgroundColor: opt.colorCode }}
                />
              )}
              {opt.label}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => removeOption(opt.value)}
              />
            </Badge>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between text-[13px] leading-[22px] font-normal h-[38px] text-[var(--primary-text-color)] hover:bg-[var(--primary-background-color)]",
              selected.length > 0 && "h-[38px] py-2"
            )}
          >
            {selectedOptions.length > 0 ? (
              isChipsRequired ? (
                <span className="text-[#ffffff] absolute text-[12px] leading-[15px] font-regular h-[20px] w-[20px] right-[37px] flex items-center justify-center bg-[var(--primary-color)] rounded-full">
                  {selectedOptions.length}
                </span>
              ) : (
                <>
                  <span className="text-[12px] leading-[22px] font-medium text-[#172b4d]">
                    Locations
                  </span>
                  <span className="text-[#ffffff] absolute text-[12px] leading-[15px] font-regular h-[20px] w-[20px] right-[37px] flex items-center justify-center bg-[var(--primary-color)] rounded-full">
                    {selectedOptions.length}
                  </span>
                </>
              )
            ) : (
              <span className="text-[12px] leading-[22px] font-normal text-[#676879]">
                {placeholder}
              </span>
            )}
            <ChevronsUpDown className="ml-auto h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          {loading ? (
            <div className="p-2 text-[13px] text-gray-500">Loading...</div>
          ) : (
            <Command>
              <CommandInput placeholder="Search..." />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {options.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      onSelect={() => toggleOption(opt.value)}
                      className="flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        readOnly
                        checked={selected.includes(opt.value)}
                        className="w-4 h-4 cursor-pointer accent-primary"
                      />
                      {opt.colorCode && (
                        <span
                          className="w-3 h-3 rounded-full border"
                          style={{ backgroundColor: opt.colorCode }}
                        />
                      )}
                      <span>{opt.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
